import { Box, Tooltip, Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import React, { useEffect } from 'react';
import { GaugeMeter } from '../GaugeMeter/GaugeMeter';
import { TimeRangeEnum } from '../FilterSection/TimeRangeSelector/types';
import { DeviceFilterEnum } from '../FilterSection/DeviceSelector/types';
import { kpiActions } from '../../actions';
import { useDispatch } from 'react-redux';
import { AppThunkDispatch } from 'src/app-types';
import { useAppSelector } from 'src/hooks/useAppSelector';

interface IRevenueDistributionProps {
  timeRange: TimeRangeEnum;
  product: string;
  device: DeviceFilterEnum;
  shopId: number;
}

export const RevenueDistribution = ({
  timeRange,
  product,
  device,
  shopId,
}: IRevenueDistributionProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { revenueDistributionValuesList } = useAppSelector(state => state.dataInsights) as any;
  const loading = revenueDistributionValuesList?.loading;
  const error = revenueDistributionValuesList?.error;

  const data = revenueDistributionValuesList?.data;
  const syteRevenue = Number(data?.syteRevenue ?? 0);
  const totalRevenue = Number(data?.totalRevenue ?? 0);

  useEffect(() => {
    dispatch(
      kpiActions.getRevenueDistribution({
        shopId,
        timeRange,
        product,
        device,
      })
    );
  }, [dispatch, timeRange, product, device, shopId]);

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Tooltip
        title='Share of total website revenue attributed to Syte. Product selection changes the Syte-attributed numerator; total website revenue remains the denominator.'
        placement='top'
      >
        <Typography variant='h6' sx={{ fontWeight: 700, mb: 2, width: 'fit-content' }}>
          Revenue Distribution
        </Typography>
      </Tooltip>
      <Box display='flex' flexGrow={1} alignItems='center' justifyContent='center'>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              minHeight: 260,
              mt: 2,
            }}
          >
            <Box sx={{ width: 280, height: 160, position: 'relative', overflow: 'hidden' }}>
              <Skeleton
                variant='circular'
                width={280}
                height={280}
                sx={{ position: 'absolute', top: 0 }}
              />
            </Box>
            <Skeleton variant='text' width={120} height={24} sx={{ mt: 2 }} />
          </Box>
        ) : error ? (
          <Box color='error.main' textAlign='center' py={4}>
            <Typography color='error'>{error}</Typography>
          </Box>
        ) : data ? (
          <GaugeMeter syteRevenue={syteRevenue} totalRevenue={totalRevenue} />
        ) : null}
      </Box>
    </Box>
  );
};
